import { useEffect, useRef } from 'react'
import { Box, CircularProgress, Typography, Alert } from '@mui/material'
import {
  createChart,
  ColorType,
  IChartApi,
  ISeriesApi,
  AreaData,
  Time,
} from 'lightweight-charts'
import { useQuery } from '@tanstack/react-query'
import { portfolioApi } from '../../services/api'
import { usePortfolioStore } from '../../store/portfolioStore'

export default function TradingChart() {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef          = useRef<IChartApi | null>(null)
  const seriesRef         = useRef<ISeriesApi<'Area'> | null>(null)
  const { selectedPortfolioId } = usePortfolioStore()

  const { data, isLoading, error } = useQuery({
    queryKey: ['equity-curve', selectedPortfolioId],
    queryFn: () =>
      portfolioApi.equityCurve(selectedPortfolioId!).then((r) => r.data),
    enabled: !!selectedPortfolioId,
  })

  // Create chart once on mount
  useEffect(() => {
    if (!chartContainerRef.current) return

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#1e293b' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: '#334155' },
        horzLines: { color: '#334155' },
      },
      timeScale: { timeVisible: true, secondsVisible: false },
      width:  chartContainerRef.current.clientWidth,
      height: 400,
    })

    const series = chart.addAreaSeries({
      lineColor:   '#3b82f6',
      topColor:    'rgba(59, 130, 246, 0.3)',
      bottomColor: 'rgba(59, 130, 246, 0.0)',
      lineWidth:   2,
    })

    chartRef.current  = chart
    seriesRef.current = series

    const ro = new ResizeObserver(() => {
      chart.applyOptions({ width: chartContainerRef.current?.clientWidth ?? 600 })
    })
    ro.observe(chartContainerRef.current)

    return () => {
      ro.disconnect()
      chart.remove()
    }
  }, [])

  // Update data when query results arrive
  useEffect(() => {
    if (!seriesRef.current || !data) return

    const chartData: AreaData<Time>[] = (data as Array<{
      timestamp: string; equity: number
    }>).map((point) => ({
      time: (new Date(point.timestamp).getTime() / 1000) as Time,
      value: point.equity,
    }))

    seriesRef.current.setData(chartData)
    chartRef.current?.timeScale().fitContent()
  }, [data])

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Equity Curve
      </Typography>

      {!selectedPortfolioId && (
        <Alert severity="info">Select a portfolio in the Dashboard first</Alert>
      )}

      {error && <Alert severity="error">Failed to load equity curve</Alert>}

      {isLoading && <CircularProgress />}

      <Box
        ref={chartContainerRef}
        sx={{
          width: '100%',
          height: 400,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #334155',
        }}
      />
    </Box>
  )
}
